import { type IBotData, type IEntityData, StateEnum } from "../../defines";
import type { IBotAction } from "../../defines/IBotAction";
import type { IBotDataSet } from "../../defines/IBotDataSet";
import { find } from "../../utils/container_help/find";
import { traversal } from "../../utils/container_help/traversal";

export class BotMaker {
  static makers = new Map<string, () => BotMaker>();
  static warn: (...args: any) => unknown = () => { };
  static register(oid: string, func: () => BotMaker) {
    this.makers.set(oid, func);
  }
  protected readonly _bot: IBotData
  constructor(oid: string) {
    this._bot = {
      id: oid,
      oid: oid,
      actions: {},
    }
  }
  get bot(): IBotData { return this._bot; }
  get frames(): Exclude<IBotData['frames'], undefined> {
    const { bot } = this;
    let frames = bot.frames
    if (!frames) frames = bot.frames = {};
    return frames;
  }
  get states(): Exclude<IBotData['states'], undefined> {
    const { bot } = this;
    let states = bot.states
    if (!states) states = bot.states = {};
    return states;
  }
  set_actions(...actions: (IBotAction | (() => IBotAction))[]): this {
    const { bot } = this
    for (const action of actions) {
      const a = typeof action === 'function' ? action() : action
      bot.actions[a.action_id] = a;
    }
    return this;
  }
  set_frames(frame_ids: (string | number)[], action_ids: string[]): this {
    const { frames } = this
    frames['' + frame_ids] = action_ids;
    return this;
  }
  set_states(state_ids: (string | number | StateEnum)[], action_ids: string[]): this {
    const { states } = this
    states['' + state_ids] = action_ids;
    return this;
  }
  check(entity: IEntityData) {
    const { states, frames, bot: { actions } } = this;
    const exists_action_ids = new Set<string>(Object.keys(actions));

    traversal(states, (states, action_ids) => {
      const state_ids = ('' + states).split(',')
      for (const state_id of state_ids) {
        const exists = !!find(entity.frames, ([_, frame]) => state_id == '' + frame.state);
        if (!exists) BotMaker.warn(`[BotBuilder::check] state "${state_id}" is not used in any frames in entity: "${entity.id}(${entity.base.name})" .`)
      }
      if (!action_ids?.length) {
        BotMaker.warn(`[BotBuilder::check] actions of states "${states}" should not be empty, but got ${action_ids}, entity: "${entity.id}(${entity.base.name})".`)
      } else for (const action_id of action_ids) {
        const exists = !!find(actions, ([aid]) => aid == '' + action_id);
        if (!exists) BotMaker.warn(`[BotBuilder::check] action "${action_id}" of states "${states}" is not exists in entity: "${entity.id}(${entity.base.name})".`)
      }
      if (exists_action_ids.size > 0) action_ids?.forEach(v => exists_action_ids.delete(v))
    })

    traversal(frames, (frames, action_ids) => {
      const frame_ids = ('' + frames).split(',')

      for (const frame_id of frame_ids) {
        const exists = Object.keys(entity.frames).some(v => v === frame_id);
        if (!exists) BotMaker.warn(`[BotBuilder::check] frame "${frame_id}" is not exists in entity: "${entity.id}(${entity.base.name})".`)
      }
      if (!action_ids?.length) {
        BotMaker.warn(`[BotBuilder::check] actions of frame "${frames}" should not be empty, but got ${action_ids}, entity: "${entity.id}(${entity.base.name})" .`)
      } else for (const action_id of action_ids) {
        const exists = !!find(actions, ([aid]) => aid == '' + action_id);
        if (!exists) BotMaker.warn(`[BotBuilder::check] action "${action_id}" of "${frames}" is not exists in entity: "${entity.id}(${entity.base.name})".`)
      }
      if (exists_action_ids.size > 0) action_ids?.forEach(v => exists_action_ids.delete(v))
    })

    if (exists_action_ids.size > 0) {
      BotMaker.warn(`[BotBuilder::check] actions "${Array.from(exists_action_ids)}" is not used in entity: "${entity.id}(${entity.base.name})"`)
    }
  }
  set_dataset(dataset: IBotDataSet): this {
    this.bot.dataset = { ...this.bot.dataset, ...dataset }
    return this;
  }
}
